import { Injectable, OnDestroy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { Subject, fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class NavigationService implements OnDestroy {
    private isNavigating = false;
    private readonly transitionDuration = 200;
    private readonly destroy$ = new Subject<void>();
    private readonly timeouts: number[] = [];

    constructor(private router: Router) {
        this.listenToRouterEvents();
        this.listenToPopState();
    }

    private listenToRouterEvents(): void {
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationStart && !this.isNavigating),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.start());

        this.router.events
            .pipe(
                filter(
                    (e) =>
                        (e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError) &&
                        this.isNavigating,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.end());
    }

    private listenToPopState(): void {
        fromEvent(window, 'popstate')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.isNavigating) return;
                this.start();
                this.timeouts.push(window.setTimeout(() => this.end(), this.transitionDuration));
            });
    }

    private start(): void {
        this.isNavigating = true;
        requestAnimationFrame(() => document.body.classList.add('page-navigating'));
    }

    private end(): void {
        this.timeouts.push(
            window.setTimeout(() => {
                requestAnimationFrame(() => {
                    document.body.classList.remove('page-navigating');
                    this.isNavigating = false;
                });
            }, 50),
        );
    }

    ngOnDestroy(): void {
        this.timeouts.forEach((id) => window.clearTimeout(id));
        this.destroy$.next();
        this.destroy$.complete();
    }
}
